import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export type ScanOutcome = "ok" | "duplicate" | "extra";
export type SessionProgress = { total: number; scanned: number; missing: number; extra: number };
export type ScanResult = {
  outcome: ScanOutcome;
  trackingNumber: string;
  recipient: string;
  progress: SessionProgress;
};
export type OrderLookup = {
  orderId: string;
  storeId: string;
  number: string | null;
  customerName: string | null;
  total: number | null;
  status: string | null;
} | null;

const KEY = "shipment-counting";

export function useShipmentSessions(status: "active" | "archived") {
  return useQuery({
    queryKey: [KEY, "list", status],
    queryFn: () => fetchJson<any[]>(`/api/shipment-counting/sessions?status=${status}`),
  });
}

export function useShipmentSession(id: string | null) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => fetchJson<any>(`/api/shipment-counting/sessions/${id}`),
    enabled: !!id,
  });
}

export function useCreateShipmentSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string }) =>
      fetchJson<any>("/api/shipment-counting/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useImportNajave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, files }: { id: string; files: File[] }) => {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      return fetchJson<{ results: { fileName: string; ok: boolean; error?: string; warning?: string | null }[] }>(
        `/api/shipment-counting/sessions/${id}/import`,
        { method: "POST", body: fd }
      );
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: [KEY, vars.id] });
    },
  });
}

export function useScanShipment(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (trackingNumber: string) =>
      fetchJson<ScanResult>(`/api/shipment-counting/sessions/${sessionId}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trackingNumber }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, sessionId] }),
  });
}

export function useArchiveShipmentSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson<any>(`/api/shipment-counting/sessions/${id}/archive`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteShipmentSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson<void>(`/api/shipment-counting/sessions/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useRemoveExtraItem(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) =>
      fetchJson<{ progress: SessionProgress }>(
        `/api/shipment-counting/sessions/${sessionId}/items/${itemId}`,
        { method: "DELETE" }
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function lookupShipmentOrder(trackingNumber: string): Promise<OrderLookup> {
  return fetchJson<OrderLookup>(
    `/api/shipment-counting/lookup/${encodeURIComponent(trackingNumber)}`
  );
}
